@if (!readonly()) {
  <div [class]="wrapperClasses">
    @if (labelText) {
      <label [class]="labelClasses" [attr.for]="inputId() || null">
        @if (resolvedLabelIcon) {
          <ng-icon [name]="resolvedLabelIcon" class="icon-size-4 text-current"></ng-icon>
        }
        <span>{{ labelText }}</span>
      </label>
    }

    <z-select
      [class]="selectHostClasses"
      [zSize]="isDesignSystemField ? 'ds' : 'default'"
      [attr.id]="inputId() || null"
      [zDisabled]="disabled() || loading()"
      [zMultiple]="type() === 'multiple'"
      [zPlaceholder]="placeholder() || ''"
      [zValue]="type() === 'multiple' ? selectedOptionKeys : (selectedOptionKeys[0] || '')"
      [zAppearance]="panelAppearance"
      (zSelectionChange)="onZardSelectionChange($event)"
    >
      <!-- Prototype `.sd-val`: placeholder, or the selected `.sd-chip`s (avatar · label · ×) on ONE line; the chips that overflow
           the line are hidden at render/resize time and counted in the `+N` `.sd-more` pill (prototype `fit()`, see
           `svz-chip-trigger-fit.ts`). -->
      <span #triggerValue zSelectTrigger class="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden whitespace-nowrap">
        @if (!selectedOptions.length) {
          <span [class]="placeholderClasses">{{ placeholder() }}</span>
        } @else {
          @for (option of selectedOptions; track option.key) {
            <span
              data-svz-trigger-chip
              [class]="triggerChipClasses + (showTriggerAvatar ? ' pl-0.5' : ' pl-2')"
            >
              @if (showTriggerAvatar) {
                <svz-chip-avatar [kind]="avatarKind" [name]="renderOptionText(option)" [picture]="pictureOf(option)" />
              }
              <span
                class="truncate"
                [attr.title]="renderOptionText(option)"
              >
                {{ renderOptionText(option) }}
              </span>
              @if (canRemoveFromTrigger) {
                <!-- The trigger is a <button>, so the remover cannot be one (nested buttons are invalid); it is still keyboard reachable. -->
                <span
                  role="button"
                  tabindex="0"
                  class="inline-flex shrink-0 rounded text-[#5a5f6b] hover:bg-[#dfe1e8]"
                  [attr.aria-label]="('Remover' | transloco) + ' ' + renderOptionText(option)"
                  (click)="removeOption(option, $event)"
                  (keydown.enter)="removeOption(option, $event)"
                  (keydown.space)="removeOption(option, $event)"
                >
                  <ng-icon name="lucideX" size="14px" aria-hidden="true" />
                </span>
              }
            </span>
          }
          @if (selectedOptions.length > 1) {
            <!-- filled by `fitTriggerChips` (text `+N`, title = hidden names); stays hidden while everything fits -->
            <span data-svz-trigger-more class="hidden inline-flex shrink-0 items-center rounded-full bg-[#eef0f4] px-2 py-0.5 text-xs font-bold text-[#444444]"></span>
          }
        }
      </span>

      @if (showSearchInput) {
        <div class="sticky top-0 z-[1] border-b border-[#f0f2f5] bg-white pb-1">
          <label [class]="searchBoxClasses()">
            <ng-icon name="svcSearch" [class]="searchIconClasses()" aria-hidden="true"></ng-icon>
            <input
              type="text"
              [value]="searchText"
              [placeholder]="(searchPlaceholder() || 'Pesquisar') | transloco"
              [attr.aria-label]="(searchPlaceholder() || 'Pesquisar') | transloco"
              (input)="onSearchInput($event)"
              class="w-full flex-1 border-0 bg-transparent text-sm leading-5 text-[#0d122f] outline-none placeholder:text-[#757575]"
            />
          </label>
        </div>
      }

      @if (!filteredOptions.length && !loading()) {
        <div class="px-3 py-2 text-sm text-[#9aa0ac]">
          {{ 'Nenhum resultado encontrado.' | transloco }}
        </div>
      } @else if (optionsRevealed()) {
        <cdk-virtual-scroll-viewport
          class="w-full [&>.cdk-virtual-scroll-content-wrapper]:w-full"
          [itemSize]="rowHeight"
          [style.height.px]="listHeight"
          (keydown)="onListKeydown($event)"
        >
          <z-select-item
            *cdkVirtualFor="let option of filteredOptions; trackBy: trackByKey"
            [zValue]="option.key"
            [attr.title]="optionTooltipEnabled() ? null : renderOptionText(option)"
            [zCheckbox]="useCheckbox"
            zAppearance="ds"
            [class]="rowClass"
            [style.height.px]="rowHeight"
          >
            @if (templateRef()) {
              <ng-container
                [ngTemplateOutlet]="templateRef()"
                [ngTemplateOutletContext]="{ option: option, rederingInList: true }"
              ></ng-container>
            } @else if (selectionIndicator() === 'check') {
              <!-- `.sd-opt`: `.sd-nm` (avatar + name, gap 10) on the left, `.sd-ck` circle-check 18 on the right when selected -->
              <span class="flex w-full items-center justify-between gap-3">
                <span class="flex min-w-0 items-center gap-2.5">
                  @if (optionAvatar() !== 'none') {
                    <svz-chip-avatar [kind]="avatarKind" [name]="renderOptionText(option)" [picture]="pictureOf(option)" [color]="colorOf(option)" />
                  }
                  <span
                    #optionTip="svzTruncatedTooltip"
                    class="truncate"
                    [svzTruncatedTooltip]="renderOptionText(option)"
                    [zTooltip]="optionTip.tooltip()"
                  >{{ renderOptionText(option) }}</span>
                </span>
                @if (selectedKeySet().has(option.key)) {
                  <ng-icon name="lucideCircleCheck" class="svz-chip-check shrink-0 text-primary" size="18px" aria-hidden="true" />
                }
              </span>
            } @else {
              {{ renderOptionText(option) }}
            }
          </z-select-item>
        </cdk-virtual-scroll-viewport>
      }
    </z-select>

    @if (showValidation()) {
      <svz-validation-message [class]="validationClasses" [control]="control"></svz-validation-message>
    }
  </div>
} @else {
  <div class="flex w-full flex-col gap-1">
    @if (labelText) {
      <div class="inline-flex items-center gap-1 text-sm font-medium text-default">
        @if (resolvedPrefixIcon) {
          <ng-icon [name]="resolvedPrefixIcon" class="icon-size-4 text-current"></ng-icon>
        }
        <span>{{ labelText }}</span>
      </div>
    }

    @if ((readonlyStyle() === 'chip' || readonlyStyle() === 'transparent-chip') && selectedOptions.length) {
      <div class="flex flex-wrap gap-2">
        @for (option of visibleReadonlyOptions; track option.key) {
          <div
            class="inline-flex max-w-full items-center rounded-full px-2 py-1 text-sm"
            [class.bg-hover]="readonlyStyle() === 'chip'"
            [class.bg-transparent]="readonlyStyle() === 'transparent-chip'"
          >
            <span [class.truncate]="truncateTextValue()">{{ renderOptionText(option) }}</span>
          </div>
        }

        @if (undisplayedSelectedOptions > 0) {
          <div class="inline-flex items-center rounded-full bg-hover px-2 py-1 text-sm text-default">
            +{{ undisplayedSelectedOptions }}
          </div>
        }
      </div>
    } @else {
      <span class="text-base font-medium text-default" [class.truncate]="truncateTextValue()">
        {{ selectedOptionsText }}
      </span>
    }
  </div>
}
